import type { ErrorResponse, ValidationIssue } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

/**
 * 서버 오류 한 건.
 *
 * 🔴 **에러 봉투가 두 꼴이다**(2026-09-17 실측 · `docs/api-survey.md` B-6).
 *   - 계약 봉투(IFC-MSG-03): `{error_code, message, suggested_actions, request_id, correlation_id}`
 *     — 401 · 422 에서 확인.
 *   - FastAPI 기본: `{"detail": "Not Found"}` — **404 가 계약을 벗어난다.**
 *   여기서 두 꼴을 다 받아 하나로 만든다. 화면은 이 타입만 본다.
 *
 * 🔴 **`request_id` 를 반드시 보존한다.** 서버가 헤더(`x-request-id`)와 본문 둘 다로 준다.
 *    백엔드에 문제를 보고할 때 이게 없으면 재현 절차가 반쪽이 된다.
 */
export class ApiError extends Error {
  readonly status: number
  /** 404 처럼 계약 봉투를 벗어난 응답에는 없다 */
  readonly error_code: string | undefined
  readonly suggested_actions: readonly string[] | undefined
  readonly request_id: string | undefined
  readonly correlation_id: string | undefined
  readonly command_id: string | undefined
  readonly violations: readonly ValidationIssue[] | undefined
  readonly context: Record<string, unknown> | undefined
  /** 요청이 실제로 나갔는지 — 네트워크 단절·타임아웃이면 false. 재시도 판단의 입력 */
  readonly transport: boolean

  constructor(init: {
    status: number
    message: string
    error_code?: string | undefined
    suggested_actions?: readonly string[] | undefined
    request_id?: string | undefined
    correlation_id?: string | undefined
    command_id?: string | undefined
    violations?: readonly ValidationIssue[] | undefined
    context?: Record<string, unknown> | undefined
    transport?: boolean
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.error_code = init.error_code
    this.suggested_actions = init.suggested_actions
    this.request_id = init.request_id
    this.correlation_id = init.correlation_id
    this.command_id = init.command_id
    this.violations = init.violations
    this.context = init.context
    this.transport = init.transport ?? false
  }

  /** 권한 없음 — 집행 지점. 화면은 버튼을 숨기지 않고 비활성 + 사유로 둔다 */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** 인증 만료 — refresh 흐름의 입력 */
  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

/** 응답이 계약 봉투인지 */
function isErrorEnvelope(body: unknown): body is ErrorResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as ErrorResponse).error_code === 'string'
  )
}

/** FastAPI 기본 `{"detail": ...}` 꼴인지. 404 가 여기로 온다 */
function detailOf(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  // 422 의 FastAPI 기본형은 배열이다. 우리 서버는 봉투를 주지만 폴백은 열어 둔다
  if (Array.isArray(detail) && detail.length > 0) return JSON.stringify(detail)
  return undefined
}

/**
 * 응답 본문 → `ApiError`.
 * ⚠ 본문이 비었거나 JSON 이 아닐 수도 있다(502 프록시 오류 등). 그때도 화면은 떠야 한다.
 */
export function toApiError(response: Response, body: unknown): ApiError {
  const requestIdHeader = response.headers.get('x-request-id') ?? undefined

  if (isErrorEnvelope(body)) {
    return new ApiError({
      status: response.status,
      message: body.message,
      error_code: body.error_code,
      suggested_actions: body.suggested_actions,
      request_id: body.request_id ?? requestIdHeader,
      correlation_id: body.correlation_id,
      command_id: body.command_id,
      violations: body.violations,
      context: body.context,
    })
  }

  const detail = detailOf(body)
  return new ApiError({
    status: response.status,
    message: detail ?? `HTTP ${String(response.status)}`,
    request_id: requestIdHeader,
  })
}

/** 응답 자체를 못 받은 경우(네트워크 단절 · 타임아웃 · CORS 차단) */
export function toTransportError(cause: unknown): ApiError {
  const aborted = cause instanceof DOMException && cause.name === 'TimeoutError'
  return new ApiError({
    status: 0,
    message: aborted ? t('error.requestTimeout') : t('error.network'),
    transport: true,
  })
}
